import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DateService } from '../date/date.service';
import { DateFormatEnum } from '../../../core/enums/date-format.enum';
import { GenderPrefixPipe } from '@shared/pipes/gender-prefix/gender-prefix.pipe';
import { ResponseAncillaryProviderGroupEnum } from '@enums/ancillary-responses.enum';
import { ContentText, ContentTable, Table, TableCell } from 'pdfmake/interfaces';
import { AncillaryService, Passenger } from '@pages/common-order/_interfaces_/passenger.interface';

@Injectable({
  providedIn: 'root',
})
export class PassengerDetailsPdfService {
  constructor(private translateService: TranslateService, private dateService: DateService, private genderPrefixPipe: GenderPrefixPipe) {}

  private getGenderPrefix(gender: string): string {
    return this.translateService.instant(this.genderPrefixPipe.transform(gender));
  }

  public getContent(passengers: Passenger[]): [ContentText, ContentText, ContentTable] {
    const passengersTable: Table = {
      body: this.getPassengersTableCellContent(passengers),
      widths: ['*', '*', '*', '*', '*'],
    };

    return [
      { text: `${this.translateService.instant('PRINT.PERSONAL_DETAILS')}`, style: 'subheader' },
      { text: `${this.translateService.instant('PRINT.PASSENGERS')}`, style: 'sectionheader' },
      { table: passengersTable, margin: [0, 0, 0, 20] },
    ];
  }

  private getPassengersTableCellContent(passengers): TableCell[][] {
    const noneString = this.translateService.instant('PRINT.NONE');
    return passengers.map((passenger) => {
      const dateOfBirth = this.dateService.formatDate(passenger.dateOfBirth, DateFormatEnum.PDFdMMMyyyy);
      const frequentFlyer = passenger.frequentFlyer?.frequentFlyerNumber
        ? `${passenger.frequentFlyer.frequentFlyerNumber} (${passenger.frequentFlyer.airlineCode})`
        : noneString;
      const printBaggage = { text: null };
      const printMeal = { text: null };
      const printSeat = { text: null };

      if (passenger.ancillaryServices && passenger.ancillaryServices.length > 0) {
        if (passenger.ancillaryServices.some((service) => service.type === ResponseAncillaryProviderGroupEnum.Baggage)) {
          printBaggage.text = this.mapAncillaryServices(
            passenger.ancillaryServices.filter((service) => service.type === ResponseAncillaryProviderGroupEnum.Baggage)
          );
        }

        if (passenger.ancillaryServices.some((service) => service.type === ResponseAncillaryProviderGroupEnum.Meals)) {
          printMeal.text = this.mapAncillaryServices(
            passenger.ancillaryServices.filter((service) => service.type === ResponseAncillaryProviderGroupEnum.Meals)
          );
        }

        if (passenger.ancillaryServices.some((service) => service.type === ResponseAncillaryProviderGroupEnum.Seat)) {
          printSeat.text = this.mapAncillaryServices(
            passenger.ancillaryServices.filter((service) => service.type === ResponseAncillaryProviderGroupEnum.Seat)
          );
        }
      }

      return [
        { text: `${this.getGenderPrefix(passenger.gender)} ${passenger.firstName} ${passenger.lastName}\n ${dateOfBirth}` },
        { text: `${this.translateService.instant('PRINT.BAGGAGE')}\n ${printBaggage.text || noneString}` },
        { text: `${this.translateService.instant('PRINT.MEAL_TYPE')}\n ${printMeal.text || noneString}` },
        { text: `${this.translateService.instant('PRINT.SEAT_PREFERENCE')}\n ${printSeat.text || noneString}` },
        { text: `${this.translateService.instant('PRINT.FREQUENT_FLYER')}\n ${frequentFlyer}` },
      ];
    });
  }

  private mapAncillaryServices(ancillaryServices: AncillaryService[]): string {
    let output = '';
    const selectedAncillary = ancillaryServices
      .map((service) => service.displayName)
      .reduce((set, displayName) => set.add(displayName), new Set());

    selectedAncillary?.forEach((displayName) => {
      output += `${displayName}\n`;
    });

    return output;
  }
}
